import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Link } from "@/components/LocalizedLink";
import { useLanguage } from "@/contexts/LanguageContext";
import FloatingAsset from "@/components/FloatingAsset";
import { SpotlightCard } from "@/components/ui/spotlight-card";
import assetDiamond from "@/assets/asset-chrome-diamond.webp";
import assetPen from "@/assets/asset-chrome-pen.webp";
import { resolveHomepageServiceCards } from "@/lib/serviceCatalog";
import { cn } from "@/lib/utils";

type ServiceItem = {
  title: string;
  description: string;
};

type ServicesSectionProps = {
  services?: ServiceItem[];
};

const bentoSpan = (index: number, total: number) => {
  if (index === 0) return "md:col-span-2 lg:col-span-2";
  if (index === total - 1) return "md:col-span-2 lg:col-span-3";
  return "lg:col-span-1";
};

const ServicesSection = ({ services }: ServicesSectionProps) => {
  const { t, lang } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const resolvedServices = resolveHomepageServiceCards(lang, services);

  return (
    <section
      ref={ref}
      className="section-y relative overflow-hidden"
      id="services"
    >
      <FloatingAsset
        src={assetDiamond}
        size={140}
        className="top-10 -right-6 opacity-40"
        floatDuration={7}
      />
      <FloatingAsset
        src={assetPen}
        size={80}
        className="bottom-40 -left-2 opacity-30"
        floatDuration={9}
      />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="section-head flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <p className="font-body text-xs tracking-[0.2em] text-muted-foreground mb-3 uppercase">
              {t("services.tag")}
            </p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-foreground">
              {t("services.title")}
            </h2>
            <p className="font-body text-sm text-muted-foreground mt-3 max-w-md leading-relaxed">
              {t("services.desc")}
            </p>
          </div>
          <Link
            to="/services"
            className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            {lang === "tr" ? "Tüm hizmetler →" : "All services →"}
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {resolvedServices.map((service, i) => (
            <motion.div
              key={`${service.slug ?? "services"}-${i}`}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.7,
                delay: i * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
              className={cn(bentoSpan(i, resolvedServices.length))}
            >
              <Link
                to={service.slug ? `/services/${service.slug}` : "/services"}
                className="block h-full group"
              >
                <SpotlightCard
                  className={cn(
                    "min-h-[220px] p-8 lg:p-10 transition-transform duration-500 group-hover:-translate-y-1",
                    i === resolvedServices.length - 1 &&
                      "lg:min-h-[180px] lg:flex-row lg:items-center lg:gap-10",
                  )}
                >
                  <div
                    className={cn(
                      "relative z-10 flex flex-col h-full",
                      i === resolvedServices.length - 1 && "lg:flex-1",
                    )}
                  >
                    <span className="font-display text-[10px] tracking-[0.25em] text-muted-foreground/70 mb-8 uppercase">
                      0{i + 1}
                    </span>
                    <h3 className="font-display text-xl font-semibold text-foreground mb-3 group-hover:text-foreground/80 transition-colors">
                      {service.title}
                    </h3>
                    <p className="font-body text-sm text-muted-foreground leading-relaxed flex-1">
                      {service.description}
                    </p>
                    <span className="font-body text-xs text-muted-foreground/70 mt-8 inline-flex items-center gap-2 group-hover:text-foreground transition-colors">
                      {lang === "tr" ? "Detayları gör" : "View details"}
                      <span className="transition-transform duration-300 group-hover:translate-x-1">
                        →
                      </span>
                    </span>
                  </div>
                  {i === resolvedServices.length - 1 ? (
                    <div
                      aria-hidden
                      className="hidden lg:flex relative z-10 h-16 w-16 shrink-0 items-center justify-center rounded-full border border-border/60 bg-secondary/40 text-2xl text-foreground/70"
                    >
                      ↗
                    </div>
                  ) : null}
                </SpotlightCard>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
